import React, { useRef, useState } from 'react';
import {
  Box, Typography, TextField, Button, MenuItem,
  FormControl, InputLabel, Select, Alert, IconButton,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import CloseIcon from '@mui/icons-material/Close';
import {
  updateFeedback, ProductFeedback, FeedbackAttachmentFailure,
  FEEDBACK_ATTACHMENT_ACCEPT, FEEDBACK_ATTACHMENT_HELP_TEXT,
} from '../../functions/productFeedbackApi';
import MarkdownDescriptionField from './MarkdownDescriptionField';

interface Props {
  ticket: ProductFeedback;
  onCancel: () => void;
  onSaved: (updated: ProductFeedback, attachmentFailures?: FeedbackAttachmentFailure[]) => void;
}

const FeedbackEditForm: React.FC<Props> = ({ ticket, onCancel, onSaved }) => {
  const [type, setType] = useState(ticket.type);
  const [title, setTitle] = useState(ticket.title);
  const [description, setDescription] = useState(ticket.description);
  const [priority, setPriority] = useState(ticket.priority || '');
  const [section, setSection] = useState(ticket.section || '');
  const [affectedUrl, setAffectedUrl] = useState(ticket.affectedUrl || '');

  // Existing attachments the user hasn't removed, tagged with their original index
  // (the stable handle the backend uses — it never sees these hydrated URLs directly).
  const totalExistingCount = ticket.images.length;
  const [keptImages, setKeptImages] = useState(
    ticket.images.map((img, index) => ({ index, url: img.url, name: img.name, available: img.available }))
  );
  const [newImageFiles, setNewImageFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const removeExistingImage = (index: number) => {
    setKeptImages(prev => prev.filter(img => img.index !== index));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setNewImageFiles(prev => [...prev, ...files]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeNewImage = (index: number) => {
    setNewImageFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!type || !title.trim() || !description.trim()) {
      setErrorMsg('Type, title, and description are required.');
      return;
    }

    setSaving(true);
    setErrorMsg('');

    const formData = new FormData();
    formData.append('type', type);
    formData.append('title', title.trim());
    formData.append('description', description.trim());
    if (priority) formData.append('priority', priority);
    if (section.trim()) formData.append('section', section.trim());
    if (affectedUrl.trim()) formData.append('affectedUrl', affectedUrl.trim());
    // Opt-in-to-remove: only indices the user actually clicked "remove" on go here. Anything
    // never shown to this form at all (e.g. a future entry beyond what we hydrated) is simply
    // never mentioned, so the backend's default is to keep it — never silently deleted.
    const keptIndexSet = new Set(keptImages.map(img => img.index));
    const removedIndices = Array.from({ length: totalExistingCount }, (_, i) => i)
      .filter(i => !keptIndexSet.has(i));
    formData.append('removedImageIndices', JSON.stringify(removedIndices));
    newImageFiles.forEach(file => formData.append('images', file));

    try {
      const resp: any = await updateFeedback(ticket._id, formData);
      if (resp?.success) {
        onSaved(resp.data, resp.attachmentFailures);
      } else {
        setErrorMsg(resp?.message || 'Update failed. Please try again.');
      }
    } catch (err: any) {
      setErrorMsg(err?.message || 'Update failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
      {errorMsg && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMsg('')}>{errorMsg}</Alert>
      )}

      <TextField
        fullWidth label="Title" value={title}
        onChange={e => setTitle(e.target.value)}
        required sx={{ mb: 2 }}
      />

      <TextField
        fullWidth label="Raised by" value={ticket.userEmail || ''}
        disabled sx={{ mb: 2 }}
      />

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <FormControl fullWidth required variant="outlined">
          <InputLabel id="edit-feedback-type-label">Type</InputLabel>
          <Select
            labelId="edit-feedback-type-label"
            label="Type"
            value={type}
            onChange={(e) => setType(e.target.value as ProductFeedback['type'])}
          >
            <MenuItem value="bug">Bug / Issue</MenuItem>
            <MenuItem value="enhancement">Enhancement</MenuItem>
            <MenuItem value="feature">New Feature</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth variant="outlined">
          <InputLabel id="edit-feedback-priority-label">Priority</InputLabel>
          <Select
            labelId="edit-feedback-priority-label"
            label="Priority"
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
          >
            <MenuItem value=""><em>None</em></MenuItem>
            <MenuItem value="high">High</MenuItem>
            <MenuItem value="medium">Medium</MenuItem>
            <MenuItem value="low">Low</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TextField
        fullWidth label="Section / Screen (optional)" value={section}
        onChange={e => setSection(e.target.value)} sx={{ mb: 2 }}
      />

      <MarkdownDescriptionField
        value={description}
        onChange={setDescription}
        required
      />

      <TextField
        fullWidth label="Affected URL (optional)" value={affectedUrl}
        onChange={e => setAffectedUrl(e.target.value)} sx={{ mb: 2 }}
        placeholder="https://..."
      />

      <input
        ref={fileInputRef}
        type="file"
        accept={FEEDBACK_ATTACHMENT_ACCEPT}
        multiple
        style={{ display: 'none' }}
        onChange={handleImageChange}
      />
      <Box sx={{ mb: 2 }}>
        <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.6)', display: 'block', mb: 0.5 }}>
          Attachments
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1 }}>
          {keptImages.map(({ index, url, name, available }) => (
            <Box
              key={`existing-${index}`}
              component={available ? 'a' : 'div'}
              href={available ? url : undefined}
              target={available ? '_blank' : undefined}
              rel={available ? 'noopener noreferrer' : undefined}
              sx={{
                display: 'inline-flex', alignItems: 'center', gap: 1, alignSelf: 'flex-start',
                px: 1.5, py: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1,
                textDecoration: 'none', color: available ? 'text.primary' : 'text.disabled', maxWidth: '100%',
              }}
            >
              <InsertDriveFileOutlinedIcon fontSize="small" sx={{ color: available ? 'text.secondary' : 'text.disabled' }} />
              <Typography variant="body2" noWrap sx={{ maxWidth: 320, fontStyle: available ? 'normal' : 'italic' }}>
                {name}{!available && ' (unavailable)'}
              </Typography>
              <IconButton
                type="button" size="small" aria-label={`Remove attachment ${name}`}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); removeExistingImage(index); }}
                sx={{ width: 22, height: 22, ml: 0.5 }}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          ))}

          {newImageFiles.map((file, i) => (
            <Box
              key={`new-${file.name}-${i}`}
              sx={{
                display: 'inline-flex', alignItems: 'center', gap: 1, alignSelf: 'flex-start',
                px: 1.5, py: 1, border: '1px solid', borderColor: 'primary.main', borderRadius: 1,
                maxWidth: '100%',
              }}
            >
              <InsertDriveFileOutlinedIcon fontSize="small" sx={{ color: 'primary.main' }} />
              <Typography variant="body2" noWrap sx={{ maxWidth: 320 }}>{file.name}</Typography>
              <IconButton
                type="button" size="small" aria-label={`Remove new attachment ${file.name}`}
                onClick={() => removeNewImage(i)}
                sx={{ width: 22, height: 22, ml: 0.5 }}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          ))}
        </Box>

        <Button
          type="button"
          variant="outlined"
          size="small"
          startIcon={<AttachFileIcon />}
          onClick={() => fileInputRef.current?.click()}
        >
          Attach Files
        </Button>
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
          {FEEDBACK_ATTACHMENT_HELP_TEXT}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button onClick={onCancel} variant="outlined" size="small" disabled={saving}>Cancel</Button>
        <Button type="submit" variant="contained" size="small" disabled={saving}>
          {saving ? 'Saving…' : 'Save Changes'}
        </Button>
      </Box>
    </Box>
  );
};

export default FeedbackEditForm;
